/**
 * bookingsList — client + types for the v2 booking LIST + STATS endpoints
 * (`GET /api/v2/bookings`, `GET /api/v2/bookings/stats`, BON-130).
 *
 * Sibling to `./bookingsV2.ts` (the single-booking detail client). Mirrors the
 * frozen BON-130 contract field-for-field: the list is PAGED and returns an
 * UNWRAPPED `{ bookings, totalCount, page, pageSize }` envelope (totalCount is
 * the full filtered count, not the page length); stats is the rolling-7-day KPI
 * window, independent of the list's filter/sort params. Region scoping is
 * applied server-side (FILTER, not block) — the FE just renders what it gets.
 */
import { apiFetch } from '../lib/apiFetch';

import type { BookingSource, BookingStatus } from './bookingsV2';

/** The `status` query-param vocabulary (coarser than the raw DB status). */
export type BookingStatusFilter = 'confirmed' | 'comp' | 'rebooked' | 'refunded' | 'canceled';

export type BookingSort = 'customer' | 'status' | 'bookingDate' | 'confirmationCode';
export type BookingSortDir = 'asc' | 'desc';

export interface BookingListItem {
  id: string;
  confirmationCode: string | null;
  /** The ref to show staff: `confirmationCode`, else a migrated booking's FareHarbor display id. */
  displayRef: string | null;
  status: BookingStatus;
  isComp: boolean;
  source: BookingSource;
  createdAt: string;
  /** ISO UTC; real purchase date (FH). Sort key for "bookingDate". */
  bookedAt: string | null;
  attendeeCount: number;
  money: { totalCents: number; currency: string };
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  workshop: {
    id: string;
    /** WorkshopType.displayName ?? name; nullable on migrated rows. */
    name: string | null;
    startsAt: string;
    /** Naive wall-clock local start (no Z) for display; null → fall back. */
    localStartDatetime: string | null;
    venue: { id: string; name: string; city: string; state: string };
    region: { id: string; name: string; parentName: string | null };
  };
}

export interface BookingListResponse {
  /** The current page of rows (≤ pageSize). */
  bookings: BookingListItem[];
  /** TOTAL region-scoped + filtered row count — drives the "N bookings" label
   *  (no longer === bookings.length now that the endpoint is paged). */
  totalCount: number;
  /** Echoed page number (1-based). */
  page: number;
  /** Echoed page size (clamped server-side to [1, 200]). */
  pageSize: number;
}

/** Default page window. The design has no pager — we request the full first
 *  page (size 200) and surface a "showing first N of M" hint if truncated. */
export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 200;

export interface BookingListParams {
  status?: BookingStatusFilter;
  source?: BookingSource;
  /** Case-insensitive substring: firstName / lastName / email / code. */
  q?: string;
  sort?: BookingSort;
  dir?: BookingSortDir;
  page?: number;
  pageSize?: number;
}

/** Rolling 7-day KPI window; region-scoped; independent of list filters. */
export interface BookingStats {
  bookingsThisWeek: number;
  bookingsDeltaPct: number | null;
  ticketsSold: number;
  ticketsOnlinePct: number;
  revenueCents: number;
  revenueDeltaPct: number | null;
  refundRatePct: number;
  refundCount: number;
  rebookRatePct: number;
  rebookCount: number;
  compsThisWeek: number;
  compPartnersCount: number;
}

/** Builds the list query string from params, omitting empties. `page`/`pageSize`
 *  default to the full first page. Exported for tests. */
export function buildListQuery(params: BookingListParams = {}): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.source) qs.set('source', params.source);
  const q = params.q?.trim();
  if (q) qs.set('q', q);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  qs.set('page', String(params.page ?? DEFAULT_PAGE));
  qs.set('pageSize', String(params.pageSize ?? DEFAULT_PAGE_SIZE));
  return `?${qs.toString()}`;
}

export async function fetchBookingsList(
  params: BookingListParams = {},
  opts: { signal?: AbortSignal } = {},
): Promise<BookingListResponse> {
  const res = await apiFetch(`/api/v2/bookings${buildListQuery(params)}`, { signal: opts.signal });
  if (!res.ok) throw new Error(`Failed to fetch bookings: ${res.status}`);
  return (await res.json()) as BookingListResponse;
}

export async function fetchBookingStats(): Promise<BookingStats> {
  const res = await apiFetch('/api/v2/bookings/stats');
  if (!res.ok) throw new Error(`Failed to fetch booking stats: ${res.status}`);
  return (await res.json()) as BookingStats;
}
